import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { useEffect, useRef } from 'react'
import {
  $createParagraphNode,
  $getRoot,
  BLUR_COMMAND,
  COMMAND_PRIORITY_LOW,
  type LexicalEditor,
} from 'lexical'

type ValuePluginProps = {
  value?: string
  onChange: (value: string) => void
  disabled?: boolean
}

let parser: DOMParser

const EMPTY_HTML_RE = /^\s*(<p><br><\/p>|<p><\/p>)\s*$/i
const normalizeHtml = (html: string) => (EMPTY_HTML_RE.test(html) ? '' : html)

const SKIP_DOM_SELECTION_TAG = 'skip-dom-selection' // existe como tag oficial  [oai_citation:2‡lexical.dev](https://lexical.dev/docs/concepts/updates?utm_source=chatgpt.com)

function isEditorFocused(editor: LexicalEditor) {
  const root = editor.getRootElement()
  if (!root) return false
  const active = document.activeElement
  return active === root || root.contains(active)
}

function applyHtml(editor: LexicalEditor, html: string) {
  const next = normalizeHtml(html ?? '')
  parser ??= new DOMParser()

  editor.update(
    () => {
      const root = $getRoot()
      root.clear()

      if (next === '') {
        root.append($createParagraphNode())
        return
      }

      const dom = parser.parseFromString(next, 'text/html')
      const nodes = $generateNodesFromDOM(editor, dom)
      root.append(...nodes)
    },
    {
      tag: ['external-sync', SKIP_DOM_SELECTION_TAG, 'skip-scroll-into-view'],
    } as any,
  )
}

function ValuePlugin({ value, onChange, disabled }: ValuePluginProps): null {
  const [editor] = useLexicalComposerContext()

  const internal = useRef<string>('')
  const hasBaseline = useRef(false)
  const pending = useRef<string | null>(null)

  useEffect(() => {
    editor.setEditable(!disabled)
  }, [disabled, editor])

  // baseline imediata (não depender de “primeiro update”)
  useEffect(() => {
    editor.getEditorState().read(() => {
      internal.current = normalizeHtml($generateHtmlFromNodes(editor, null))
      hasBaseline.current = true
    })
  }, [editor])

  // sync externo (value pode chegar depois)
  useEffect(() => {
    if (value == null) return
    const next = normalizeHtml(value)

    if (!hasBaseline.current) {
      pending.current = next
      return
    }

    if (next === internal.current) return

    // se está focado, evita mexer durante digitação
    if (isEditorFocused(editor)) {
      pending.current = next
      return
    }

    pending.current = null
    internal.current = next
    applyHtml(editor, next)
  }, [value, editor])

  // aplica pendente no blur
  useEffect(() => {
    return editor.registerCommand(
      BLUR_COMMAND,
      () => {
        const next = pending.current
        if (next != null && next !== internal.current) {
          pending.current = null
          internal.current = next
          applyHtml(editor, next)
        }
        return false
      },
      COMMAND_PRIORITY_LOW,
    )
  }, [editor])

  // listener de mudanças internas (ignora updates externos e updates só de seleção)
  useEffect(() => {
    return editor.registerUpdateListener(
      ({ editorState, dirtyElements, dirtyLeaves, tags, prevEditorState }) => {
        if (prevEditorState == null) return
        if (tags?.has?.('external-sync')) return
        if (dirtyLeaves.size === 0 && dirtyElements.size === 0) return

        editorState.read(() => {
          const html = normalizeHtml($generateHtmlFromNodes(editor, null))
          if (html === internal.current) return
          internal.current = html
          if (!disabled) onChange(html)
        })
      },
    )
  }, [editor, disabled, onChange])

  return null
}

export { ValuePlugin }
export { ValuePluginProps }
